import { Badge, BadgeOwnProps, Box, ListItemButton, ListItemIcon } from '@mui/material';
import { Permissions } from 'Permission';
import React from 'react';
import { NavLink } from 'react-router-dom';
import { useSidebarTone } from '../helpers/SidebarContext';
import { Palette } from 'Palette';
import { IconMap, IconType } from './IconHelper';

export interface SidebarItemProps extends React.RefAttributes<HTMLDivElement> {
  to: string;
  label: string;
  icon?: IconType;
  selected: boolean;
  dense?: boolean;
  children?: React.ReactNode;
  expanded?: boolean;
  onHoverIntent?: (hasChildren: boolean) => void;
  forMobile?: boolean;
  permissionKey?: Permissions;
  onClick?: () => void;
  badgeCount?: number | { [key in NonNullable<BadgeOwnProps['color']>]?: number };
  notificationComponent?: React.ReactNode;
  disabled?: boolean;
}

const SidebarItem = React.forwardRef<HTMLDivElement, SidebarItemProps>(
  (
    {
      to,
      label,
      icon,
      selected,
      dense,
      children,
      expanded: _expanded,
      onHoverIntent,
      forMobile,
      onClick,
      badgeCount,
      notificationComponent,
      disabled = false,
    },
    ref,
  ) => {
    const [hovered, setHovered] = React.useState(false);
    const hasChildren = Boolean(children);

    const contextTone = useSidebarTone();
    const tone = contextTone;

    const handleEnter = () => {
      setHovered(true);
      if (onHoverIntent) onHoverIntent(hasChildren);
    };

    const handleLeave = () => {
      setHovered(false);
      if (onHoverIntent) onHoverIntent(false);
    };

    const icons = icon ? IconMap[icon] : undefined;

    const isMultiBadge =
      badgeCount !== undefined &&
      typeof badgeCount === 'object' &&
      Object.keys(badgeCount).length > 0 &&
      Object.values(badgeCount).some((value) => value > 0);

    const IconToRender = icon
      ? selected || hovered
        ? icons!.iconRounded
        : icons!.icon
      : undefined;

    const fullMultiBadge = () => {
      let currentBadge: React.ReactNode | null = null;

      // Precalculate leftmost badge and offset each badge to the right
      const badgeEntries = Object.entries(
        badgeCount as { [key in NonNullable<BadgeOwnProps['color']>]: number },
      ).filter(([_, count]) => count > 0);

      const baseIcon = IconToRender ? <IconToRender /> : null;
      const badgeOffset = 18; // px, adjust as needed for spacing
      const totalBadges = badgeEntries.length;

      if (totalBadges === 0) {
        currentBadge = baseIcon;
      } else {
        // Start with the leftmost badge and wrap each subsequent badge to the right
        currentBadge = badgeEntries.reduce((acc, [color, count], idx) => {
          return (
            <Badge
              badgeContent={count}
              color={color as BadgeOwnProps['color']}
              sx={{
                '& .MuiBadge-badge': {
                  right: -(3 - idx * badgeOffset),
                  top: 3,
                  fontSize: '0.65rem',
                  minWidth: '16px',
                  height: '16px',
                  zIndex: 10 + idx,
                },
              }}
            >
              {acc}
            </Badge>
          );
        }, baseIcon);
      }

      return currentBadge;
    };

    return (
      <Box sx={{ position: 'relative' }} ref={ref}>
        {notificationComponent}
        <ListItemButton
          component={NavLink}
          to={to}
          onClick={() => onClick?.()}
          selected={selected}
          disabled={disabled}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
          sx={{
            color: tone ? Palette[tone].text : 'inherit',
            opacity: disabled ? 0.4 : 1,
            pointerEvents: disabled ? 'none' : 'auto',
            '& .MuiListItemIcon-root': {
              color: tone ? Palette[tone].text : 'inherit',
            },
            pl: dense ? 6 : 2,
            justifyContent: dense ? 'start' : 'flex-start',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            {IconToRender && !forMobile && (
              <ListItemIcon
                sx={{
                  justifyContent: 'center',
                  minWidth: 'unset',
                  color: tone ? Palette[tone].text : 'inherit',
                }}
              >
                {badgeCount !== undefined &&
                ((typeof badgeCount === 'number' && badgeCount > 0) ||
                  (typeof badgeCount === 'string' && badgeCount !== '0')) ? (
                  <Badge
                    badgeContent={badgeCount}
                    color="info"
                    sx={{
                      '& .MuiBadge-badge': {
                        right: -3,
                        top: 3,
                        fontSize: '0.65rem',
                        minWidth: '16px',
                        height: '16px',
                      },
                    }}
                  >
                    <IconToRender />
                  </Badge>
                ) : isMultiBadge ? (
                  fullMultiBadge()
                ) : IconToRender ? (
                  <IconToRender />
                ) : null}
              </ListItemIcon>
            )}
            {
              <Box
                className="sidebar-item-label"
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: selected ? 700 : 400,
                  fontSize: {
                    xs: forMobile ? '1.33rem' : '0.75rem',
                    md: '0.75rem',
                  },
                  textAlign: 'center',
                  mt: 0.5,
                  lineHeight: 1.2,
                  color: { xs: forMobile ? Palette.tone1.text : undefined },
                }}
              >
                {label}
              </Box>
            }
          </Box>
        </ListItemButton>
      </Box>
    );
  },
);

export default SidebarItem;
